// WpFileManager — browse / edit / upload / download / manage files on a WP site
// over SFTP or FTP (see backend wpDev.js). This is how a dev pushes code changes
// without leaving the platform: edit a file in place, or upload a build.
import { useState, useEffect, useCallback } from "react";
import { Folder, FileText, Upload, FolderPlus, RefreshCw, Download, Trash2, Edit2, CornerLeftUp, Save, X } from "lucide-react";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (path, opts = {}) =>
  fetch(API + path, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...opts });

const fmtSize = (b) => b == null ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b > 1024 ? `${(b / 1024).toFixed(1)} KB` : `${b} B`;
const EDITABLE = /\.(php|js|jsx|ts|tsx|css|scss|less|html?|xml|json|ya?ml|md|txt|ini|conf|conf|env|htaccess|sh|sql|py|rb|vue|svg|log)$/i;

export default function WpFileManager({ siteId, protocol, fileRoot }) {
  const [path, setPath]     = useState("");
  const [data, setData]     = useState(null);   // {path, entries}
  const [loading, setLoad]  = useState(false);
  const [err, setErr]       = useState("");
  const [busy, setBusy]     = useState("");
  const [editing, setEditing] = useState(null);  // {path, content, dirty}
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async (p) => {
    setLoad(true); setErr("");
    try {
      const r = await apiFetch(`/wp/sites/${siteId}/fm/list?path=${encodeURIComponent(p || "")}`);
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Could not list directory"); setLoad(false); return; }
      setData(d); setPath(d.path);
    } catch { setErr("Request failed"); }
    finally { setLoad(false); }
  }, [siteId]);

  useEffect(() => { load(""); }, [load]);

  const cwd = data?.path || "";
  const parent = cwd.includes("/") ? cwd.replace(/\/[^/]*$/, "") || "/" : "";
  const atRoot = fileRoot ? (cwd === fileRoot) : (cwd === "/" || cwd === "." || cwd === "");

  const go = (name) => load(cwd.endsWith("/") ? cwd + name : cwd + "/" + name);

  const openFile = async (name) => {
    setBusy("read:" + name); setErr("");
    try {
      const full = cwd.endsWith("/") ? cwd + name : cwd + "/" + name;
      const r = await apiFetch(`/wp/sites/${siteId}/fm/read?path=${encodeURIComponent(full)}`);
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Could not open file"); return; }
      setEditing({ path: full, name, content: d.content, dirty: false });
    } finally { setBusy(""); }
  };

  const saveFile = async () => {
    if (!editing) return;
    setBusy("save"); setErr("");
    try {
      const r = await apiFetch(`/wp/sites/${siteId}/fm/write`, { method: "PUT", body: JSON.stringify({ path: editing.path, content: editing.content }) });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Save failed"); return; }
      setEditing(e => ({ ...e, dirty: false }));
    } finally { setBusy(""); }
  };

  const download = async (name) => {
    const full = cwd.endsWith("/") ? cwd + name : cwd + "/" + name;
    const r = await fetch(`${API}/wp/sites/${siteId}/fm/download?path=${encodeURIComponent(full)}`, { headers: { Authorization: `Bearer ${tok()}` } });
    if (!r.ok) { setErr("Download failed"); return; }
    const blob = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
  };

  const doUpload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      for (const f of fileList) fd.append("files", f);
      fd.append("path", cwd);
      const r = await fetch(`${API}/wp/sites/${siteId}/fm/upload`, { method: "POST", headers: { Authorization: `Bearer ${tok()}` }, body: fd });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Upload failed"); return; }
      if (d.quarantined > 0) setErr(`${d.uploaded} uploaded · ${d.quarantined} quarantined by the malware scanner`);
      load(cwd);
    } finally { setUploading(false); }
  };

  const mkdir = async () => {
    const name = window.prompt("New folder name:");
    if (!name) return;
    const full = cwd.endsWith("/") ? cwd + name : cwd + "/" + name;
    const r = await apiFetch(`/wp/sites/${siteId}/fm/mkdir`, { method: "POST", body: JSON.stringify({ path: full }) });
    if (!r.ok) { const d = await r.json(); setErr(d.error || "mkdir failed"); return; }
    load(cwd);
  };

  const rename = async (name) => {
    const to = window.prompt("Rename / move to:", name);
    if (!to || to === name) return;
    const from = cwd.endsWith("/") ? cwd + name : cwd + "/" + name;
    const dest = to.startsWith("/") ? to : (cwd.endsWith("/") ? cwd + to : cwd + "/" + to);
    const r = await apiFetch(`/wp/sites/${siteId}/fm/rename`, { method: "POST", body: JSON.stringify({ from, to: dest }) });
    if (!r.ok) { const d = await r.json(); setErr(d.error || "Rename failed"); return; }
    load(cwd);
  };

  const del = async (e) => {
    if (!window.confirm(`Delete ${e.dir ? "folder" : "file"} "${e.name}"?${e.dir ? " (must be empty)" : ""}`)) return;
    const full = cwd.endsWith("/") ? cwd + e.name : cwd + "/" + e.name;
    const r = await apiFetch(`/wp/sites/${siteId}/fm/delete`, { method: "DELETE", body: JSON.stringify({ path: full, isDir: e.dir }) });
    if (!r.ok) { const d = await r.json(); setErr(d.error || "Delete failed"); return; }
    load(cwd);
  };

  const iconBtn = { background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "#64748b", borderRadius: 6 };
  const tool = { display: "inline-flex", alignItems: "center", gap: 6, background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };
  const inputId = `wpfm-upload-${siteId}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>{protocol === "ftp" ? "FTP" : "SFTP"}</span>
        <code style={{ flex: 1, minWidth: 160, fontSize: 12, color: "var(--t-text-body)", background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "6px 10px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cwd || "/"}</code>
        <label htmlFor={inputId} style={tool}><Upload size={13} /> {uploading ? "Uploading…" : "Upload"}</label>
        <input id={inputId} type="file" multiple style={{ display: "none" }} onChange={e => { doUpload(e.target.files); e.target.value = ""; }} />
        <button style={tool} onClick={mkdir}><FolderPlus size={13} /> New folder</button>
        <button style={tool} onClick={() => load(cwd)}><RefreshCw size={13} /></button>
      </div>

      {err && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171", borderRadius: 8, padding: "8px 12px", fontSize: 12.5 }}>{err}</div>}

      {/* Listing */}
      <div style={{ border: "1px solid var(--t-border-soft)", borderRadius: 10, overflow: "hidden" }}>
        {!atRoot && (
          <div onClick={() => load(parent)} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 14px", cursor: "pointer", borderBottom: "1px solid var(--t-border-soft)", color: "#94a3b8", fontSize: 13 }}>
            <CornerLeftUp size={15} /> ..
          </div>
        )}
        {loading && <div style={{ padding: 20, color: "#475569", fontSize: 13 }}>Loading…</div>}
        {!loading && data?.entries?.length === 0 && <div style={{ padding: 20, color: "#475569", fontSize: 13 }}>Empty directory.</div>}
        {!loading && (data?.entries || []).map(e => (
          <div key={e.name} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 14px", borderBottom: "1px solid var(--t-border-soft)", fontSize: 13 }}>
            {e.dir
              ? <Folder size={15} style={{ color: "#fbbf24", flexShrink: 0 }} />
              : <FileText size={15} style={{ color: "#60a5fa", flexShrink: 0 }} />}
            <span onClick={() => e.dir ? go(e.name) : (EDITABLE.test(e.name) ? openFile(e.name) : download(e.name))}
              style={{ flex: 1, minWidth: 0, color: "var(--t-text-body)", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={e.name}>
              {e.name}{busy === "read:" + e.name ? " …" : ""}
            </span>
            <span style={{ fontSize: 11, color: "#475569", flexShrink: 0, width: 70, textAlign: "right" }}>{e.dir ? "" : fmtSize(e.size)}</span>
            <span style={{ display: "flex", gap: 2, flexShrink: 0 }}>
              {!e.dir && EDITABLE.test(e.name) && <button style={iconBtn} title="Edit" onClick={() => openFile(e.name)}><Edit2 size={14} /></button>}
              {!e.dir && <button style={iconBtn} title="Download" onClick={() => download(e.name)}><Download size={14} /></button>}
              <button style={iconBtn} title="Rename / move" onClick={() => rename(e.name)}><CornerLeftUp size={14} style={{ transform: "rotate(90deg)" }} /></button>
              <button style={{ ...iconBtn, color: "#f87171" }} title="Delete" onClick={() => del(e)}><Trash2 size={14} /></button>
            </span>
          </div>
        ))}
      </div>

      {/* Editor modal */}
      {editing && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 20 }}>
          <div style={{ background: "var(--t-panel-hi)", border: "1px solid var(--t-border-soft)", borderRadius: 12, width: "100%", maxWidth: 900, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--t-border-soft)", display: "flex", alignItems: "center", gap: 10 }}>
              <FileText size={15} style={{ color: "#60a5fa" }} />
              <code style={{ flex: 1, fontSize: 12.5, color: "var(--t-text-primary)", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{editing.path}{editing.dirty ? " •" : ""}</code>
              <button onClick={saveFile} disabled={busy === "save" || !editing.dirty}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: editing.dirty ? "linear-gradient(135deg,#10b981,#0891b2)" : "var(--t-surface-mid)", color: editing.dirty ? "#fff" : "#64748b", border: "none", borderRadius: 7, padding: "6px 16px", fontSize: 12.5, fontWeight: 700, cursor: editing.dirty ? "pointer" : "default", fontFamily: "inherit" }}>
                <Save size={13} /> {busy === "save" ? "Saving…" : "Save"}
              </button>
              <button onClick={() => { if (!editing.dirty || window.confirm("Discard unsaved changes?")) setEditing(null); }}
                style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", display: "flex" }}><X size={18} /></button>
            </div>
            <textarea value={editing.content} onChange={e => setEditing(ed => ({ ...ed, content: e.target.value, dirty: true }))}
              spellCheck={false} autoFocus
              style={{ flex: 1, minHeight: 380, resize: "none", border: "none", outline: "none", background: "#0b1120", color: "#e2e8f0", padding: 16, fontSize: 12.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", lineHeight: 1.6, borderRadius: "0 0 12px 12px" }} />
          </div>
        </div>
      )}
    </div>
  );
}
